import React, { useState } from 'react'
import { useDebounce } from '@/hooks/useDebounce'
import { useAuth } from '@/hooks/useAuth'
import { useSalaryAccess } from '@/hooks/useSalaryAccess'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { MetricCard } from '@/components/MetricCard'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { HTMLSelect as Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { ReassignManagerModal } from '@/components/workforce/ReassignManagerModal'
import { HighlightedText } from '@/components/common/HighlightedText'
import { SkeletonCard } from '@/components/common/SkeletonCard'
import { useEmployees, useEmployeeStats, useDepartments, useRoles } from '@/hooks/useWorkforce'
import { useReassignManager } from '@/hooks/useWorkforceMutations'
import { useWorkforceUIStore } from '@/stores/workforce-ui-store'
import { Employee } from '@/types/workforce'
import { Link } from '@tanstack/react-router'
import {
  Users,
  UserPlus,
  Search,
  Filter,
  Download,
  MoreHorizontal,
  Mail,
  Phone,
  Calendar,
  Building2,
  Briefcase,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  UserCog,
  Edit,
  X,
  Loader2,
  RefreshCw
} from 'lucide-react'

function EmployeesContent() {
  const { user } = useAuth()
  const { canViewSalaries, formatSalaryValue } = useSalaryAccess()

  // 🚀 Server State (TanStack Query)
  const {
    employeesFilters,
    setEmployeesFilters,
    resetEmployeesFilters,
    employeesSort,
    setEmployeesSort
  } = useWorkforceUIStore()

  const {
    employees,
    loading,
    isFetching,
    error,
    nextToken,
    refetch
  } = useEmployees(
    {
      status: employeesFilters.status === 'all' ? undefined : employeesFilters.status,
      departmentId: employeesFilters.departmentId,
      search: employeesFilters.search
    },
    {
      limit: 300,
      sortBy: employeesSort.sortBy,
      sortOrder: employeesSort.sortOrder
    }
  )

  const { stats, loading: statsLoading } = useEmployeeStats()
  const { departments, loading: departmentsLoading } = useDepartments()
  const { roles, loading: rolesLoading } = useRoles()

  // 🔄 Mutations
  const reassignManagerMutation = useReassignManager()

  // 🎯 Local State (ephemeral UI state only)
  const [showFilters, setShowFilters] = useState(false)
  const [searchTerm, setSearchTerm] = useState(employeesFilters.search || '')
  const [employeeToReassign, setEmployeeToReassign] = useState<Employee | null>(null)
  const [currentManagerForModal, setCurrentManagerForModal] = useState<Employee | undefined>(undefined)

  // Debounced search to avoid excessive API calls
  const debouncedSearch = useDebounce(searchTerm, 500)

  // Update filters when debounced search changes
  React.useEffect(() => {
    setEmployeesFilters({ search: debouncedSearch || undefined })
  }, [debouncedSearch, setEmployeesFilters])

  // Debug: log employees
  React.useEffect(() => {
    console.log('>>> EMPLOYEES FROM API:', employees.length, employees)
  }, [employees])

  // Derived state
  const isLoading = loading || statsLoading || departmentsLoading || rolesLoading
  const hasNextPage = !!nextToken

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return <CheckCircle className="w-4 h-4 text-success" />
      case 'INACTIVE':
        return <XCircle className="w-4 h-4 text-brand-gray" />
      case 'PENDING':
        return <Clock className="w-4 h-4 text-warning" />
      case 'TERMINATED':
        return <AlertTriangle className="w-4 h-4 text-error" />
      default:
        return <Clock className="w-4 h-4 text-brand-gray" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-success/20 text-success'
      case 'INACTIVE':
        return 'bg-brand-gray/20 text-brand-gray'
      case 'PENDING':
        return 'bg-warning/20 text-warning'
      case 'TERMINATED':
        return 'bg-error/20 text-error'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'N/A'
    try {
      const dateObj = new Date(date)
      if (isNaN(dateObj.getTime())) return 'N/A'
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(dateObj)
    } catch {
      return 'N/A'
    }
  }

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount)
  }

  // Handlers
  const handleReassignManager = async (employee: Employee) => {
    setEmployeeToReassign(employee)

    // Load current manager if exists
    if (employee.employment?.managerId) {
      try {
        // Try to find current manager in the already loaded employees list
        const manager = employees.find(emp => emp.employeeId === employee.employment.managerId)
        setCurrentManagerForModal(manager)
      } catch (error) {
        console.error('Error loading current manager:', error)
        setCurrentManagerForModal(undefined)
      }
    } else {
      setCurrentManagerForModal(undefined)
    }
  }

  const handleReassignConfirm = async (newManagerId: string | null) => {
    if (!employeeToReassign) return

    try {
      // ✅ Use mutation hook - automatically handles cache invalidation and toasts
      await reassignManagerMutation.mutateAsync({
        employeeId: employeeToReassign.employeeId,
        newManagerId
      })

      // Close modal
      setEmployeeToReassign(null)
      setCurrentManagerForModal(undefined)
    } catch (error) {
      // Error toast is handled by mutation hook
      console.error('Error reassigning manager:', error)
      throw error // Re-throw to let modal handle the error display
    }
  }

  const handleReassignCancel = () => {
    setEmployeeToReassign(null)
    setCurrentManagerForModal(undefined)
  }

  return (
    <>
    <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2">
              Colaboradores
            </h1>
            <p className="font-body text-brand-gray">
              Gerencie informações dos colaboradores da empresa
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <Button variant="ghost" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
            <Link to={"/workforce/employees/new" as any}>
              <Button size="sm">
                <UserPlus className="w-4 h-4 mr-2" />
                Novo Colaborador
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="dashboard-grid lg:grid-cols-4">
            <MetricCard
              title="Total de Colaboradores"
              value={stats.totalEmployees}
              icon={Users}
              trend="neutral"
            />
            <MetricCard
              title="Colaboradores Ativos"
              value={stats.activeEmployees}
              change={`${Math.round((stats.activeEmployees / stats.totalEmployees) * 100)}%`}
              icon={CheckCircle}
              trend="up"
            />
            <MetricCard
              title="Demitidos"
              value={stats.terminatedEmployees}
              icon={AlertTriangle}
              trend="neutral"
            />
            <MetricCard
              title="Inativos"
              value={stats.inactiveEmployees}
              icon={XCircle}
              trend="neutral"
            />
          </div>
        )}

        {/* Search and Filters */}
        <Card className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-gray pointer-events-none z-10" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  style={{ paddingLeft: '3rem' }}
                />
              </div>
            </div>
            <div className="flex gap-3">
              <select
                value={employeesFilters.departmentId?.toString() || ''}
                onChange={(e) => {
                  const value = e.target.value
                  console.log('>>> Department select changed:', value)
                  setEmployeesFilters({ departmentId: value || undefined })
                }}
                className="form-select px-3 py-2 border border-brand-gray/30 rounded-monumenta bg-brand-dark-lighter text-white"
              >
                <option value="">Todos os departamentos</option>
                {departments.map(dept => (
                  <option key={dept.departmentId} value={dept.departmentId}>{dept.name}</option>
                ))}
              </select>
              <Select
                value={employeesFilters.status || 'all'}
                onValueChange={(value) => setEmployeesFilters({ status: value as any })}
              >
                <option value="all">Todos os status</option>
                <option value="ACTIVE">Ativo</option>
                <option value="INACTIVE">Inativo</option>
                <option value="TERMINATED">Demitido</option>
              </Select>
              {(employeesFilters.departmentId || employeesFilters.status !== 'all' || employeesFilters.search) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    resetEmployeesFilters()
                    setSearchTerm('')
                  }}
                  title="Limpar filtros"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)}>
                <Filter className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Employee List */}
        {/* 🚀 Background refresh indicator (subtle, non-blocking) */}
        {isFetching && !loading && (
          <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-800 px-3 py-1.5 rounded-md mb-4">
            <RefreshCw className="w-3 h-3 animate-spin" />
            <span>Sincronizando...</span>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            <SkeletonCard count={8} />
          </div>
        ) : employees.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nenhum colaborador encontrado"
            description="Não há colaboradores que correspondam aos critérios de busca."
            action={
              <Link to={"/workforce/employees/new" as any}>
                <Button>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Adicionar Primeiro Colaborador
                </Button>
              </Link>
            }
          />
        ) : (
          <>
            <div className="grid gap-4">
              {employees.map((employee) => {
                const name = employee.personalInfo?.name || 'Nome não informado'
                const nameParts = name.split(' ').filter(p => p.length > 0)
                const initials = nameParts.length >= 2 ?
                  `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}` :
                  nameParts.length === 1 ?
                  `${nameParts[0][0]}${nameParts[0][1] || 'N'}` :
                  'NA'

                return (
                  <Card key={employee.employeeId} className="card-hover p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-brand-dark">
                            {initials.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <Link to={`/workforce/employees/${employee.employeeId}` as any}>
                              <h3 className="font-display text-lg font-semibold text-white hover:text-brand-primary transition-colors">
                                {name}
                              </h3>
                            </Link>
                            {employee.employment?.status && (
                              <Badge className={getStatusColor(employee.employment.status)}>
                                <div className="flex items-center gap-1">
                                  {getStatusIcon(employee.employment.status)}
                                  <span className="capitalize">{employee.employment.status.toLowerCase()}</span>
                                </div>
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-brand-gray">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              <span className="truncate">{employee.email || 'N/A'}</span>
                            </div>
                            {employee.personalInfo.phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4" />
                                <span>{employee.personalInfo.phone}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4" />
                              <span>Dept. {employee.employment?.department?.name || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Briefcase className="w-4 h-4" />
                              <span>Cargo {employee.employment?.role?.name || 'N/A'}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 mt-3 text-sm text-brand-gray">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>Admissão: {formatDate(employee.employment?.startDate)}</span>
                            </div>
                            <div>
                              <span className={canViewSalaries ? '' : 'text-gray-400'}>
                                Salário: {formatSalaryValue(employee.employment?.salary)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleReassignManager(employee)}
                          className="border-brand-primary/30 hover:bg-brand-primary/10 hover:border-brand-primary text-brand-primary"
                        >
                          <UserCog className="w-4 h-4 mr-1.5" />
                          <span className="hidden sm:inline">Gestor</span>
                        </Button>
                        <Link to={`/workforce/employees/${employee.employeeId}/edit` as any}>
                          <Button variant="ghost" size="sm">
                            <Edit className="w-4 h-4 sm:mr-1.5" />
                            <span className="hidden sm:inline">Editar</span>
                          </Button>
                        </Link>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                )
              })}
            </div>

            {/* Load More Button */}
            {hasNextPage && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="ghost"
                  onClick={() => refetch()}
                  disabled={isFetching}
                >
                  {isFetching ? 'Carregando...' : 'Carregar Mais'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Reassign Manager Modal */}
      {employeeToReassign && (
        <ReassignManagerModal
          employee={employeeToReassign}
          currentManager={currentManagerForModal}
          onConfirm={handleReassignConfirm}
          onCancel={handleReassignCancel}
        />
      )}
    </>
  )
}

// Apply RBAC protection - only admin can access workforce employees
export default withPageProtection(EmployeesContent, PAGES.WORKFORCE_EMPLOYEES)